import { useCallback, useEffect, useState } from 'react'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { getRuntime } from './_mode'
import type { WorkspaceRunSummary } from './types'
import { api, ApiError } from '../api/client'
import { captureHookFailure, recordHookOutcome, withSpan } from '../observability/sentry'

export interface StudioRunsState {
  groupedCounts: Record<string, number>
  latestRun: WorkspaceRunSummary | null
  runs: WorkspaceRunSummary[]
  workspaceIds: string[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

function emptyCounts(): Record<string, number> {
  return {
    document: 0,
    pdf: 0,
    presentation: 0,
    spreadsheet: 0,
    notes: 0,
    build: 0,
  }
}

export function useStudioRuns(projectId: string | undefined): StudioRunsState {
  const [groupedCounts, setGroupedCounts] = useState<Record<string, number>>(emptyCounts())
  const [latestRun, setLatestRun] = useState<WorkspaceRunSummary | null>(null)
  const [runs, setRuns] = useState<WorkspaceRunSummary[]>([])
  const [workspaceIds, setWorkspaceIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!projectId) {
      setGroupedCounts(emptyCounts())
      setLatestRun(null)
      setRuns([])
      setWorkspaceIds([])
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)

    const { live } = getRuntime()
    if (!live) {
      setGroupedCounts(emptyCounts())
      setLatestRun(null)
      setRuns([])
      setWorkspaceIds([])
      setLoading(false)
      recordHookOutcome('useStudioRuns', 'demo_fallback', {
        route: '/api/workspace/runs',
        rows: 0,
      })
      return
    }

    const startedAt = Date.now()
    try {
      const result = await api.workspace.runs({ project_id: projectId, limit: 40 })
      setGroupedCounts({ ...emptyCounts(), ...(result.grouped_counts || {}) })
      setLatestRun(result.latest_run)
      setRuns(result.runs || [])
      setWorkspaceIds(result.workspace_ids || [])
      recordHookOutcome('useStudioRuns', 'live_success', {
        route: '/api/workspace/runs',
        rows: result.runs?.length || 0,
        durationMs: Date.now() - startedAt,
      })
    } catch (err) {
      const apiErr = err as ApiError
      setGroupedCounts(emptyCounts())
      setLatestRun(null)
      setRuns([])
      setWorkspaceIds([])
      setError(apiErr?.message || 'workspace_runs_failed')
      captureHookFailure('useStudioRuns', 'live_error', err, {
        route: '/api/workspace/runs',
        code: apiErr?.code,
        status: apiErr?.status,
        durationMs: Date.now() - startedAt,
      })
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => {
    let cancelled = false
    let channel: RealtimeChannel | null = null
    const { live, supabase } = getRuntime()

    void load()

    if (!live || !supabase || !projectId) return

    try {
      channel = supabase
        .channel(`square-shell-studio-runs-${projectId}`)
        .on(
          'postgres_changes' as any,
          { event: '*', schema: 'public', table: 'sq_workspace_runs' },
          async () => {
            if (cancelled) return
            try {
              await withSpan('sq_workspace_runs.refresh', 'db.query', load)
            } catch (err) {
              captureHookFailure('useStudioRuns', 'realtime_failure', err, {
                table: 'sq_workspace_runs',
                route: '/api/workspace/runs',
              })
            }
          },
        )
        .subscribe()
    } catch (err) {
      captureHookFailure('useStudioRuns', 'realtime_failure', err, {
        table: 'sq_workspace_runs',
        route: '/api/workspace/runs',
      })
    }

    return () => {
      cancelled = true
      if (channel && supabase) supabase.removeChannel(channel)
    }
  }, [load, projectId])

  return {
    groupedCounts,
    latestRun,
    runs,
    workspaceIds,
    loading,
    error,
    refresh: load,
  }
}
